import type { PageGroup, Range, STDocument } from '@st/sdk'
import { deepEqual } from '@st/util/deep-equal'
import { match } from '@st/pattern'
import type { STFolderModuleState } from '../../st-folder-types'
import {
  selSelectedDoc,
  selAlreadySplitDocuments,
  selFolderOperationInProgress
} from '../../st-folder-selectors'
import { editablePageGroups } from '../../update-page-groups'

/** Opening a document selects ranges that have not already been extracted. */
function initialSelection(groups: PageGroup[], extracted: STDocument[]): Range[] {
  return groups
    .map((group) => group.pageRange)
    .filter((range) => !extracted.some((doc) => deepEqual(doc.source?.pageRange, range)))
}

/** Selection follows pages across boundary edits: splitting a selected range
 * selects both children; joining selected and unselected ranges selects the join.
 * Extraction completion changes the tags, not the user's selection. Changing
 * documents initializes selection from the extracted documents again.
 */
function projectSelection(
  selection: Range[],
  groups: PageGroup[],
  extracted: STDocument[],
  extracting: boolean
) {
  const rows = groups.map((group) => {
    const range = group.pageRange
    return {
      range,
      selected: selection.some((item) => item.first <= range.last && item.last >= range.first),
      extracted: extracted.find((doc) => deepEqual(doc.source?.pageRange, range))
    }
  })
  const selectedRanges = rows.filter((row) => row.selected).map((row) => row.range)
  return {
    rows,
    selectedRanges,
    phase: match({ extracting, empty: selectedRanges.length === 0 })
      .with({ extracting: true }, () => 'extracting' as const)
      .with({ empty: true }, () => 'empty' as const)
      .otherwise(() => 'ready' as const)
  }
}

/** Checkboxes remain editable during extraction; they affect the next request. */
export function reduceBundleSelection(
  selection: Range[],
  action: { type: 'selectionToggled'; range: Range },
  groups: PageGroup[]
): Range[] {
  const { selectedRanges } = projectSelection(selection, groups, [], false)
  return match(action)
    .with({ type: 'selectionToggled' }, ({ range }) =>
      match(selectedRanges.some((item) => deepEqual(item, range)))
        .with(true, () => selectedRanges.filter((item) => !deepEqual(item, range)))
        .with(false, () => [...selectedRanges, range].toSorted((a, b) => a.first - b.first))
        .exhaustive()
    )
    .exhaustive()
}

/** The view and transitions read the same effective selection, including queued boundary edits. */
export function selBundleEditing(state: STFolderModuleState) {
  if (state.status != 'ready') throw new Error('Bundle sidebar requires a ready folder')
  const document = selSelectedDoc(state)!
  const groups = editablePageGroups(document)
  const extracted = selAlreadySplitDocuments(state, document.id)
  const selection = state.bundleSelection ?? initialSelection(groups, extracted)
  return {
    ...projectSelection(
      selection,
      groups,
      extracted,
      selFolderOperationInProgress(state, 'folders/splitBundledDocument')
    ),
    groups,
    documentTypes: state.folderState.documentTypes
  }
}
